import type { SearchProvider, SearchResult } from './search-provider';

/** Zoom applied when flying to an exact coordinate. */
const COORDINATE_ZOOM = 12;

/**
 * Resolves decimal lat/lon queries (offline, no API). Accepts "lat, lon",
 * "lat lon", and optional N/S/E/W hemisphere letters (e.g. "38.89N, 77.03W").
 */
export const coordinateProvider: SearchProvider = {
  id: 'coordinate',
  search: async (query) => {
    const result = parseLatLon(query);
    return result ? [result] : [];
  },
};

/** Parse a coordinate pair into a result, or null when the query is not coordinates. */
function parseLatLon(query: string): SearchResult | null {
  const tokens = tokenize(query);
  if (tokens.length !== 2) return null;

  const lat = toSignedDegrees(tokens[0], 'N', 'S');
  const lon = toSignedDegrees(tokens[1], 'E', 'W');
  if (lat === null || lon === null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;

  return {
    label: `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
    sublabel: 'Coordinate',
    lat,
    lon,
    source: 'Coordinates',
    zoom: COORDINATE_ZOOM,
  };
}

/** Split a query into tokens on commas and whitespace (no regex). */
function tokenize(query: string): string[] {
  return query.split(',').join(' ').split(' ').filter((token) => token.length > 0);
}

/** Convert a token to signed decimal degrees, honoring a hemisphere letter. */
function toSignedDegrees(token: string, positive: string, negative: string): number | null {
  const { body, sign } = stripHemisphere(token.toUpperCase(), positive, negative);
  const value = Number(body);
  if (!Number.isFinite(value)) return null;
  return value * sign;
}

/** Remove a leading/trailing hemisphere letter and return the numeric body + sign. */
function stripHemisphere(
  token: string,
  positive: string,
  negative: string,
): { body: string; sign: number } {
  if (token.startsWith(negative) || token.endsWith(negative)) {
    return { body: trimLetter(token, negative), sign: -1 };
  }
  if (token.startsWith(positive) || token.endsWith(positive)) {
    return { body: trimLetter(token, positive), sign: 1 };
  }
  return { body: token, sign: 1 };
}

/** Strip a single hemisphere letter from either end of the token. */
function trimLetter(token: string, letter: string): string {
  if (token.startsWith(letter)) return token.slice(1).trim();
  if (token.endsWith(letter)) return token.slice(0, -1).trim();
  return token;
}
